import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Box,
  Chip,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { ExerciseService } from '../../services/exerciseService';
import type { Exercise } from '../../services/types';
import ExercisePickerDialog from './ExercisePickerDialog';

interface ExerciseAlternativesDialogProps {
  open: boolean;
  exercise: Exercise | null;
  onClose: () => void;
  /** Called after any add/remove so the parent list can refresh counts. */
  onChanged?: () => void;
}

/**
 * Manage the interchangeable (swappable) alternatives of an exercise, e.g.
 * Bulgarian Split Squats ↔ Step-ups / Reverse Lunge. The relationship is
 * symmetric on the backend, so linking here also links the reverse direction.
 */
const ExerciseAlternativesDialog: React.FC<ExerciseAlternativesDialogProps> = ({
  open,
  exercise,
  onClose,
  onChanged,
}) => {
  const [alternatives, setAlternatives] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadAlternatives = useCallback(async () => {
    if (!exercise) return;
    setLoading(true);
    setError(null);
    try {
      const data = await ExerciseService.getAlternatives(exercise.id);
      setAlternatives(data.alternatives);
    } catch (err) {
      console.error('Failed to load alternatives:', err);
      setError('Неуспешно зареждане на алтернативите.');
    } finally {
      setLoading(false);
    }
  }, [exercise]);

  useEffect(() => {
    if (open && exercise) {
      loadAlternatives();
    } else if (!open) {
      setAlternatives([]);
      setError(null);
    }
  }, [open, exercise, loadAlternatives]);

  const handleAdd = async (picked: Exercise) => {
    if (!exercise) return;
    setPickerOpen(false);
    if (picked.id === exercise.id) {
      setError('Упражнението не може да е алтернатива на себе си.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await ExerciseService.addAlternative(exercise.id, picked.id);
      setAlternatives(data.alternatives);
      onChanged?.();
    } catch (err) {
      console.error('Failed to add alternative:', err);
      setError('Неуспешно добавяне на алтернатива.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (altId: number) => {
    if (!exercise) return;
    setSaving(true);
    setError(null);
    try {
      await ExerciseService.removeAlternative(exercise.id, altId);
      setAlternatives((prev) => prev.filter((a) => a.id !== altId));
      onChanged?.();
    } catch (err) {
      console.error('Failed to remove alternative:', err);
      setError('Неуспешно премахване на алтернатива.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <SwapIcon color="primary" />
            <span>Алтернативи: {exercise?.name}</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Взаимозаменяеми упражнения, които могат да бъдат сменени бързо по време на
            тренировка (напр. Bulgarian Split Squats ↔ Step-ups). Връзката е двупосочна.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {(loading || saving) && <LinearProgress sx={{ mb: 2 }} />}

          {alternatives.length === 0 && !loading ? (
            <Box sx={{ py: 3, textAlign: 'center', color: 'text.disabled' }}>
              <Typography variant="body2">Все още няма зададени алтернативи.</Typography>
            </Box>
          ) : (
            <List dense>
              {alternatives.map((alt) => (
                <ListItem
                  key={alt.id}
                  secondaryAction={
                    <IconButton edge="end" color="error" onClick={() => handleRemove(alt.id)} disabled={saving}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={alt.name}
                    secondary={
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {alt.primaryMuscleGroup && <Chip label={alt.primaryMuscleGroup} size="small" variant="outlined" />}
                        {alt.equipment && <Chip label={alt.equipment} size="small" variant="outlined" />}
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => setPickerOpen(true)}
            disabled={!exercise || saving}
            sx={{ mt: 2 }}
          >
            Добави алтернатива
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Затвори</Button>
        </DialogActions>
      </Dialog>

      <ExercisePickerDialog
        open={pickerOpen}
        title="Избери алтернативно упражнение"
        description="Търси упражнение, което може да замени текущото."
        confirmLabel="Добави като алтернатива"
        initialMuscleGroup={exercise?.primaryMuscleGroup || exercise?.muscleGroup}
        onConfirm={handleAdd}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
};

export default ExerciseAlternativesDialog;
